import { EEthereumNetworkId } from '@ankr.com/provider';
import BigNumber from 'bignumber.js';
import config from 'config';

import { ApiTask, AutomateApi } from 'modules/api';
import { ContractsManager, convertFromWei } from 'modules/core';
import { api, cacheTags } from 'modules/store';

export interface GetTasksItem {
  id: string;
  name: string;
  cronTarget?: string;
  target: string;
  admin: string;
  amountSpent: BigNumber;
  isPaused: boolean;
  chainId: EEthereumNetworkId;
}

export type GetTasksResult = GetTasksItem[];

export const {
  useGetTasksQuery,
  endpoints: { getTasks },
} = api.injectEndpoints({
  endpoints: build => ({
    getTasks: build.query<
      GetTasksResult,
      { accountAddress: string; chainId: EEthereumNetworkId | null }
    >({
      queryFn: async ({ accountAddress }) => {
        async function getTasks(
          chainId: EEthereumNetworkId,
        ): Promise<GetTasksResult> {
          const sdk = await ContractsManager.getInstance({
            forceRead: true,
            chainId,
          });
          const api = new AutomateApi(chainId);
          const registry = await sdk.getRegistryContract();

          const taskIds = await registry.methods
            .userTasks(accountAddress)
            .call();

          const idApiTasks = new Map<string, ApiTask>();
          try {
            const apiTasks = await api.getAccountTasks(accountAddress);

            apiTasks.forEach(task => idApiTasks.set(task.id, task));
          } catch (error) {
            if (error instanceof Error) {
              // eslint-disable-next-line no-console
              console.error(error.message);
            }
          }

          return Promise.all(
            taskIds.map(async id => {
              const {
                target,
                executeGas,
                checkData,
                balance,
                lastAutomate,
                admin,
                maxValidBlocknumber,
                amountSpent,
                isPaused,
              } = await registry.methods.getTask(id).call();

              const { name = 'Empty Name', cronTarget } =
                idApiTasks.get(id) ?? {};

              return {
                id,
                chainId,
                name,
                target,
                executeGas,
                checkData,
                balance: convertFromWei(balance),
                lastAutomate,
                admin,
                cronTarget,
                maxValidBlocknumber,
                amountSpent: convertFromWei(amountSpent),
                isPaused,
              };
            }),
          );
        }

        const chainIds = Object.keys(config);
        const results = await Promise.all(
          chainIds.map(chainId =>
            getTasks(Number(chainId) as EEthereumNetworkId).catch(() => []),
          ),
        );

        return {
          data: results.reduce<GetTasksResult>(
            (tasks, result) => [...result, ...tasks],
            [],
          ),
        };
      },
      providesTags: [cacheTags.taskData],
    }),
  }),
});
